import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  GoogleLogo,
  CheckCircle,
  XCircle,
  ArrowRight,
  ChartLineUp,
  Cursor,
  Eye,
  TrendUp,
  ArrowsClockwise,
  Plug,
  PlugsConnected,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GoogleIntegrationsPage({ embedded = false }) {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  // GSC state
  const [gscProps, setGscProps] = useState([]);
  const [gscSelected, setGscSelected] = useState('');
  const [gscData, setGscData] = useState(null);
  const [gscLoading, setGscLoading] = useState(false);

  // GA4 state
  const [ga4Props, setGa4Props] = useState([]);
  const [ga4Selected, setGa4Selected] = useState('');
  const [ga4Data, setGa4Data] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(false);

  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    [],
  );

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/google/auth/status`, { headers: getHeaders() });
      setStatus(data);
    } catch (err) {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchStatus();
    // Handle ?google=success / ?google=error redirects
    const g = params.get('google');
    if (g === 'success') {
      toast.success('Google koblet til! 🎉');
      params.delete('google');
      setParams(params, { replace: true });
    } else if (g === 'error') {
      const reason = params.get('reason') || 'unknown';
      toast.error(`Google-tilkobling feilet: ${reason}`);
      params.delete('google');
      params.delete('reason');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/api/google/auth/login`, { headers: getHeaders() });
      window.location.href = data.auth_url;
    } catch {
      toast.error('Kunne ikke starte Google-tilkobling');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Koble fra Google? Lagrede tokens slettes.')) return;
    try {
      await axios.post(`${API}/api/google/auth/disconnect`, {}, { headers: getHeaders() });
      toast.success('Frakoblet');
      setStatus({ connected: false });
      setGscProps([]); setGa4Props([]); setGscData(null); setGa4Data(null);
    } catch {
      toast.error('Frakobling feilet');
    }
  };

  // ----- GSC -----
  const loadGscProps = async () => {
    setGscLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/google/gsc/properties`, { headers: getHeaders() });
      setGscProps(data.properties || []);
      if (!gscSelected && data.properties?.length) {
        // Prefer verified property matching tittinngarn.no
        const preferred = data.properties.find((p) => p.verified && p.site_url.includes('tittinngarn'));
        setGscSelected((preferred || data.properties[0]).site_url);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke hente GSC properties');
    } finally { setGscLoading(false); }
  };

  const fetchGscData = async () => {
    if (!gscSelected) return;
    setGscLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/google/gsc/search-analytics`,
        { site_url: gscSelected, dimensions: ['page'], row_limit: 100 },
        { headers: getHeaders() },
      );
      setGscData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'GSC-spørring feilet');
    } finally { setGscLoading(false); }
  };

  // ----- GA4 -----
  const loadGa4Props = async () => {
    setGa4Loading(true);
    try {
      const { data } = await axios.get(`${API}/api/google/ga4/properties`, { headers: getHeaders() });
      setGa4Props(data.properties || []);
      if (!ga4Selected && data.properties?.length) setGa4Selected(data.properties[0].property_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke hente GA4 properties');
    } finally { setGa4Loading(false); }
  };

  const fetchGa4Data = async () => {
    if (!ga4Selected) return;
    setGa4Loading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/google/ga4/page-metrics`,
        { property_id: ga4Selected },
        { headers: getHeaders() },
      );
      setGa4Data(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'GA4-spørring feilet');
    } finally { setGa4Loading(false); }
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-8 w-64 bg-muted animate-pulse rounded" /><div className="h-64 bg-muted animate-pulse rounded-md" /></div>;
  }

  // Not connected — show connect card
  if (!status?.connected) {
    return (
      <div className="space-y-6 max-w-3xl" data-testid="google-page">
        {!embedded && (
          <div>
            <h1 className="font-heading text-4xl tracking-tight font-extrabold flex items-center gap-3">
              <GoogleLogo size={36} weight="bold" /> Google-integrasjoner
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Koble til Google Search Console + Analytics 4 for å se reelle SEO-tall direkte i dashbordet.
            </p>
          </div>
        )}

        <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Plug size={32} weight="bold" className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl font-bold mb-2">Ikke koblet til</h3>
                <p className="text-sm text-muted-foreground mb-5">
                  Klikk under for å koble Google-kontoen din. Sortilize ber kun om{' '}
                  <strong>read-only</strong>-tilgang til Search Console og Analytics. Du kan koble fra når som helst.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1.5 mb-6">
                  <li className="flex items-center gap-2"><CheckCircle size={14} className="text-success" /> Klikk, visninger, CTR og posisjon per side</li>
                  <li className="flex items-center gap-2"><CheckCircle size={14} className="text-success" /> Sesjoner, fluktrate, konvertering fra GA4</li>
                  <li className="flex items-center gap-2"><CheckCircle size={14} className="text-success" /> AutoGuard prioriterer sider med dårlig CTR</li>
                </ul>
                <Button onClick={connect} disabled={connecting} className="gap-2" data-testid="connect-google-btn">
                  <GoogleLogo size={16} weight="bold" />
                  {connecting ? 'Sender til Google...' : 'Koble til Google'}
                  <ArrowRight size={14} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connected — show metrics
  return (
    <div className="space-y-6" data-testid="google-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          {!embedded && (
            <h1 className="font-heading text-4xl tracking-tight font-extrabold flex items-center gap-3">
              <GoogleLogo size={36} weight="bold" /> Google-integrasjoner
            </h1>
          )}
          <p className={`text-sm text-muted-foreground ${embedded ? '' : 'mt-1'} flex items-center gap-2 flex-wrap`}>
            <Badge variant="default" className="gap-1"><PlugsConnected size={12} weight="bold" /> Tilkoblet</Badge>
            <span className="font-mono text-xs">{status.google_email}</span>
            <span>· siden {new Date(status.connected_at).toLocaleDateString('nb-NO')}</span>
          </p>
        </div>
        <Button variant="outline" onClick={disconnect} className="gap-2 text-destructive" data-testid="disconnect-btn">
          <XCircle size={14} /> Koble fra
        </Button>
      </div>

      <Tabs defaultValue="gsc" className="space-y-4">
        <TabsList>
          <TabsTrigger value="gsc" className="gap-2" data-testid="tab-gsc">
            <ChartLineUp size={14} /> Search Console
          </TabsTrigger>
          <TabsTrigger value="ga4" className="gap-2" data-testid="tab-ga4">
            <TrendUp size={14} /> Analytics 4
          </TabsTrigger>
        </TabsList>

        {/* ---- GSC ---- */}
        <TabsContent value="gsc" className="space-y-4">
          {gscProps.length === 0 ? (
            <Card className="border border-border">
              <CardContent className="p-8 text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Hent dine GSC-properties for å vise søkedata fra Google.
                </p>
                <Button onClick={loadGscProps} disabled={gscLoading} className="gap-2" data-testid="load-gsc-btn">
                  <ArrowsClockwise size={14} className={gscLoading ? 'animate-spin' : ''} />
                  {gscLoading ? 'Henter...' : 'Hent GSC properties'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border border-border">
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  <Select value={gscSelected} onValueChange={setGscSelected}>
                    <SelectTrigger className="w-full sm:w-[420px]" data-testid="gsc-property-select">
                      <SelectValue placeholder="Velg property" />
                    </SelectTrigger>
                    <SelectContent>
                      {gscProps.map((p) => (
                        <SelectItem key={p.site_url} value={p.site_url}>
                          {p.site_url} {p.verified ? '✓' : '⚠'} ({p.permission_level})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchGscData} disabled={gscLoading} className="gap-2" data-testid="fetch-gsc-btn">
                    <ArrowsClockwise size={14} className={gscLoading ? 'animate-spin' : ''} />
                    Hent data (siste 30 dager)
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadGscProps} className="gap-1">
                    <ArrowsClockwise size={12} /> Oppdater liste
                  </Button>
                </CardContent>
              </Card>

              {gscData && (
                <>
                  {/* Totals */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Totale klikk" value={gscData.totals?.clicks?.toLocaleString('nb-NO')} icon={Cursor} />
                    <StatCard label="Totale visninger" value={gscData.totals?.impressions?.toLocaleString('nb-NO')} icon={Eye} />
                    <StatCard label="Snitt CTR" value={`${(gscData.totals?.ctr * 100).toFixed(1)}%`} icon={TrendUp} />
                    <StatCard label="Antall sider" value={gscData.row_count} icon={ChartLineUp} />
                  </div>

                  {/* Top pages */}
                  <Card className="border border-border">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Topp sider ({gscData.start_date} → {gscData.end_date})</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto max-h-[600px]">
                        <table className="w-full text-sm" data-testid="gsc-table">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-left p-3 font-semibold">Side</th>
                              <th className="text-right p-3 font-semibold">Klikk</th>
                              <th className="text-right p-3 font-semibold">Visn.</th>
                              <th className="text-right p-3 font-semibold">CTR</th>
                              <th className="text-right p-3 font-semibold">Pos.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gscData.rows.slice(0, 100).map((r, i) => (
                              <tr key={i} className="border-t border-border hover:bg-muted/20">
                                <td className="p-3 truncate max-w-[400px]">
                                  <a href={r.page} target="_blank" rel="noreferrer" className="hover:text-primary truncate block">
                                    {r.page?.replace(/^https?:\/\/[^/]+/, '')}
                                  </a>
                                </td>
                                <td className="p-3 text-right font-mono">{r.clicks}</td>
                                <td className="p-3 text-right font-mono text-muted-foreground">{r.impressions}</td>
                                <td className="p-3 text-right font-mono">{(r.ctr * 100).toFixed(1)}%</td>
                                <td className="p-3 text-right font-mono">{r.position?.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
        </TabsContent>

        {/* ---- GA4 ---- */}
        <TabsContent value="ga4" className="space-y-4">
          {ga4Props.length === 0 ? (
            <Card className="border border-border">
              <CardContent className="p-8 text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Hent dine GA4-properties for å vise sesjon- og konverteringsdata.
                </p>
                <Button onClick={loadGa4Props} disabled={ga4Loading} className="gap-2" data-testid="load-ga4-btn">
                  <ArrowsClockwise size={14} className={ga4Loading ? 'animate-spin' : ''} />
                  {ga4Loading ? 'Henter...' : 'Hent GA4 properties'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border border-border">
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  <Select value={ga4Selected} onValueChange={setGa4Selected}>
                    <SelectTrigger className="w-full sm:w-[420px]" data-testid="ga4-property-select">
                      <SelectValue placeholder="Velg property" />
                    </SelectTrigger>
                    <SelectContent>
                      {ga4Props.map((p) => (
                        <SelectItem key={p.property_id} value={p.property_id}>
                          {p.display_name} ({p.property_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchGa4Data} disabled={ga4Loading} className="gap-2" data-testid="fetch-ga4-btn">
                    <ArrowsClockwise size={14} className={ga4Loading ? 'animate-spin' : ''} />
                    Hent data (siste 30 dager)
                  </Button>
                </CardContent>
              </Card>

              {ga4Data && (
                <Card className="border border-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Topp sider etter sesjoner ({ga4Data.start_date} → {ga4Data.end_date})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto max-h-[600px]">
                      <table className="w-full text-sm" data-testid="ga4-table">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-left p-3 font-semibold">Side</th>
                            <th className="text-right p-3 font-semibold">Sesj.</th>
                            <th className="text-right p-3 font-semibold">Visn.</th>
                            <th className="text-right p-3 font-semibold">Fluktrate</th>
                            <th className="text-right p-3 font-semibold">Engasj.</th>
                            <th className="text-right p-3 font-semibold">Konv.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ga4Data.pages.slice(0, 100).map((p, i) => (
                            <tr key={i} className="border-t border-border hover:bg-muted/20">
                              <td className="p-3 truncate max-w-[400px]">
                                <span className="block truncate" title={p.page_title}>{p.page_path}</span>
                                <span className="text-xs text-muted-foreground truncate block">{p.page_title}</span>
                              </td>
                              <td className="p-3 text-right font-mono">{p.sessions}</td>
                              <td className="p-3 text-right font-mono text-muted-foreground">{p.page_views}</td>
                              <td className="p-3 text-right font-mono">{(p.bounce_rate * 100).toFixed(0)}%</td>
                              <td className="p-3 text-right font-mono">{(p.engagement_rate * 100).toFixed(0)}%</td>
                              <td className="p-3 text-right font-mono">{p.conversions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="border border-border">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-md">
          <Icon size={20} weight="bold" className="text-primary" />
        </div>
        <div>
          <p className="font-heading text-2xl font-extrabold">{value ?? '—'}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
